import React from 'react'
import {nanoid} from 'nanoid'
import {isFunctionalUpdate} from '@dudagroup/editor'
import {IconButton, Panel} from 'rsuite'

import MinusSquareO from '@rsuite/icons/legacy/MinusSquareO'
import Arrows from '@rsuite/icons/legacy/Arrows'
import PlusCircle from '@rsuite/icons/legacy/PlusCircle'

import {
  DndContext,
  closestCenter,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {useTranslation} from 'react-i18next'
import type {JSX} from 'react'

export interface FieldProps<V = any> {
  readonly value: V
  readonly onChange: React.Dispatch<React.SetStateAction<V>>
}

export interface ListFieldProps<T = any> extends FieldProps<ListValue<T>[]> {
  readonly label?: string
  readonly defaultValue: T | (() => T)
  readonly disabled?: boolean
  readonly children: (props: FieldProps<T>) => JSX.Element
}

export interface ListValue<T = any> {
  readonly id: string
  readonly value: T
}

export interface ListItemProps<T = any> {
  readonly value: ListValue<T>
  readonly itemIndex: number
  readonly itemDisabled?: boolean
  readonly onChange: (index: number, value: React.SetStateAction<ListValue<T>>) => void
  readonly onRemove: (index: number) => void
  readonly children: (props: FieldProps<T>) => JSX.Element
}

/** Sortable row using dnd-kit */
function SortableRow<T>({
  value,
  itemIndex,
  itemDisabled,
  onChange,
  onRemove,
  children,
}: ListItemProps<T>) {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: value.id,
  })

  const style: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 10,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  }

  function handleValueChange(fieldValue: React.SetStateAction<any>) {
    onChange(itemIndex, (prev) => ({
      ...prev,
      value: isFunctionalUpdate(fieldValue) ? fieldValue(prev.value) : fieldValue,
    }))
  }

  function handleRemove() {
    onRemove(itemIndex)
  }

  return (
    <div ref={setNodeRef} style={style}>
      <div style={{marginRight: 10}}>
        <IconButton
          {...attributes}
          {...listeners}
          icon={<Arrows />}
          disabled={itemDisabled}
          aria-label="Drag to reorder"
          size={'sm'}
        />
      </div>

      <Panel bodyFill style={{width: '100%'}}>
        <div style={{minHeight: '100%'}}>
          {children({value: value.value, onChange: handleValueChange})}
        </div>
      </Panel>

      <div style={{marginLeft: 10}}>
        <IconButton
          icon={<MinusSquareO />}
          onClick={handleRemove}
          disabled={itemDisabled}
          aria-label="Remove item"
        />
      </div>
    </div>
  )
}

/** ListInput rebuilt with dnd-kit */
export function ListInput<T>({
  value,
  label,
  defaultValue,
  disabled,
  children,
  onChange,
}: ListFieldProps<T>) {
  const {t} = useTranslation()

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: 6}}),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  )

  function handleItemChange(itemIndex: number, itemValue: React.SetStateAction<ListValue<T>>) {
    onChange((prev) =>
      Object.assign([], prev, {
        [itemIndex]: isFunctionalUpdate(itemValue) ? itemValue(prev[itemIndex]) : itemValue,
      })
    )
  }

  function handleAdd() {
    const next = typeof defaultValue === 'function' ? (defaultValue as () => T)() : defaultValue
    onChange((prev) => [...prev, {id: nanoid(), value: next}])
  }

  function handleRemove(itemIndex: number) {
    onChange((prev) => prev.filter((_, i) => i !== itemIndex))
  }

  function onDragEnd(event: DragEndEvent) {
    const {active, over} = event
    if (!over || active.id === over.id) return

    const oldIndex = value.findIndex((v) => v.id === String(active.id))
    const newIndex = value.findIndex((v) => v.id === String(over.id))
    if (oldIndex === -1 || newIndex === -1) return

    onChange((prev) => arrayMove(prev, oldIndex, newIndex))
  }

  return (
    <div>
      {label && <label>{label}</label>}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={value.map((v) => v.id)} strategy={rectSortingStrategy}>
          {value.map((item, index) => (
            <SortableRow
              key={item.id}
              value={item}
              itemIndex={index}
              itemDisabled={disabled}
              onChange={handleItemChange}
              onRemove={handleRemove}>
              {children}
            </SortableRow>
          ))}
        </SortableContext>
      </DndContext>

      <IconButton
        icon={<PlusCircle />}
        onClick={handleAdd}
        disabled={disabled}
        appearance="ghost"
        aria-label="Add item">
        {t('global.buttons.add')}
      </IconButton>
    </div>
  )
}
